import { useEffect, useRef } from "react";
import { Authenticated, useList, useTranslate } from "@refinedev/core";
import { Alert, Col, Flex, Row, Typography } from "antd";
import { EmptyState } from "@libertify/ui";
import { SubPageHeader } from "components/SubPageHeader/";
import { StudioTopBar } from "components/layout/header/StudioTopBar";
import { useParams } from "react-router-dom";
import {
  PresetDateRangePicker,
  getRange,
} from "components/PresetDateRangePicker/";
import { TrendUpIcon } from "@phosphor-icons/react";
import { CompactStatCard } from "../components/analytics/CompactStatCard";
import { SessionStatistics } from "types/";
import dayjs from "dayjs";
import { ANALYTICS_MAX_DATE_RANGE_MONTHS } from "constants/";
import { ANALYTICS_STALE_TIME } from "../components/analytics/constants/constants";
import { DEFAULT_ANALYTICS_DATE_RANGE } from "../constants";
import { VideoSummary } from "../components/analytics/VideoSummary";
import { DocumentSummary } from "../components/analytics/DocumentSummary";
import { ChatbotSummary } from "../components/analytics/ChatbotSummary";
import { SessionsChart } from "../components/analytics/SessionsChart";
import { AnalyticsTabs } from "../components/analytics/AnalyticsTabs";
import { useAnalyticsDateRange } from "./hooks/useAnalyticsDateRange";
import { useAnalyticsBack } from "./hooks/useAnalyticsBack";
import { useProjectPublications } from "./hooks/useProjectPublications";
import { useSession } from "hooks/useSession";
import { InsightsPanel } from "../analytics/components/InsightsPanel/InsightsPanel";

const AUTO_EXPAND_FALLBACK_RANGE = "12M" as const;

export function AnalyticsDashboardPage() {
  const t = useTranslate();
  const { id: projectIdParam } = useParams<{ id: string }>();
  const projectId = projectIdParam || "";
  const { currentOrganization } = useSession();
  const organizationId = currentOrganization?.id;
  const { dateRange, setDateRange, project } =
    useAnalyticsDateRange(projectIdParam);
  // Stores the project we already auto-expanded for, so the fallback fires at
  // most once per project. The :id route reuses this component across project
  // changes without remounting, so a plain boolean would never reset.
  const expandedForProjectRef = useRef<string | null>(null);

  const handleBack = useAnalyticsBack();

  const {
    data: sessionStatistics,
    isSuccess,
    isError,
    isLoading: isSessionsLoading,
    isFetching: isSessionsFetching,
    isRefetching: isSessionsRefetching,
  } = useList<SessionStatistics>({
    resource: `analytics/projects/${projectId}/sessions/v2`,
    config: {
      filters: [
        {
          field: "from_date",
          operator: "eq",
          value: dateRange.startDate?.format("YYYY-MM-DD"),
        },
        {
          field: "to_date",
          operator: "eq",
          value: dateRange.endDate?.format("YYYY-MM-DD"),
        },
      ],
    },
    queryOptions: {
      enabled: !!projectId && !!dateRange.startDate && !!dateRange.endDate,
      staleTime: ANALYTICS_STALE_TIME,
    },
    // currently api not working in dev env
    errorNotification: false,
  });
  const sessionPoints = sessionStatistics?.data ?? [];
  const sessionsData =
    sessionPoints.length > 0
      ? {
          sessions: sessionPoints.reduce((sum, p) => sum + p.sessions, 0),
          unique_visitors: sessionPoints.reduce(
            (sum, p) => sum + p.unique_visitors,
            0
          ),
          chat_interactions: sessionPoints.reduce(
            (sum, p) => sum + p.chat_interactions,
            0
          ),
          average_duration:
            sessionPoints.reduce(
              (sum, p) => sum + p.average_duration * p.sessions,
              0
            ) / sessionPoints.reduce((sum, p) => sum + p.sessions, 0),
        }
      : null;

  // Auto-expand: if the default range comes back empty,  switch the range to "12M"
  useEffect(() => {
    const isDefaultRange = dateRange.type === DEFAULT_ANALYTICS_DATE_RANGE;
    const resolvedEmpty =
      isSuccess &&
      !isSessionsLoading &&
      !isSessionsFetching &&
      sessionPoints.length === 0;

    if (
      isDefaultRange &&
      resolvedEmpty &&
      expandedForProjectRef.current !== projectId
    ) {
      expandedForProjectRef.current = projectId;
      const [startDate, endDate] = getRange(AUTO_EXPAND_FALLBACK_RANGE);
      setDateRange({ type: AUTO_EXPAND_FALLBACK_RANGE, startDate, endDate });
    }
  }, [
    isSuccess,
    isSessionsLoading,
    isSessionsFetching,
    sessionPoints.length,
    dateRange.type,
    projectId,
    setDateRange,
  ]);

  const { isInstanceLive } = useProjectPublications(project?.id);
  const hasUnpublishedInstances =
    project?.media.some((media) => !isInstanceLive(media.instance_id)) ||
    false;

  return (
    <Authenticated key="projectAnalytics">
      <SubPageHeader
        itemId={project?.id}
        itemTitle={project?.title || ""}
        page="analytics"
        source="projects"
        onBack={handleBack}
        rightContent={<StudioTopBar />}
        spaced
      />
      <Flex vertical gap={24}>
        <AnalyticsTabs />
        <Flex gap={20} align="flex-start">
          <Flex vertical gap={24} style={{ flex: "1 1 auto", minWidth: 0 }}>
            <PresetDateRangePicker
              value={{
                type: dateRange.type || DEFAULT_ANALYTICS_DATE_RANGE,
                startDate: dateRange.startDate!,
                endDate: dateRange.endDate!,
              }}
              onChange={setDateRange}
              dateFormat="L"
              minDate={dayjs().subtract(
                ANALYTICS_MAX_DATE_RANGE_MONTHS,
                "months"
              )}
              disabled={
                isSessionsLoading || isSessionsFetching || isSessionsRefetching
              }
            />

            <Typography.Title
              level={3}
              style={{ margin: 0, fontWeight: "normal" }}
            >
              {t("media.projects.analytics.dashboard.title")}
            </Typography.Title>
            {(isSuccess || isError) &&
              !sessionsData &&
              !isSessionsLoading &&
              !isSessionsFetching && (
                <Flex
                  justify="center"
                  align="center"
                  style={{ marginTop: 100 }}
                >
                  <EmptyState
                    icon={<TrendUpIcon size={80} />}
                    title={t("media.projects.analytics.dashboard.noData")}
                    description={t(
                      "media.projects.analytics.dashboard.noDataHint"
                    )}
                  />
                </Flex>
              )}
            {(sessionsData || isSessionsLoading || isSessionsFetching) && (
              <>
                {/* <Flex gap={16} style={{ width: "100%" }} justify="space-between"> */}
                <Row gutter={[8, 8]} style={{ width: "100%" }}>
                  <Col flex={1}>
                    <CompactStatCard
                      loading={isSessionsLoading || isSessionsFetching}
                      label={t("media.projects.analytics.dashboard.avgSession")}
                      metric={sessionsData?.average_duration}
                      type="minutes"
                    />
                  </Col>
                  <Col flex={1}>
                    <CompactStatCard
                      loading={isSessionsLoading || isSessionsFetching}
                      label={t(
                        "media.projects.analytics.dashboard.totalSessions"
                      )}
                      metric={sessionsData?.sessions}
                    />
                  </Col>
                  <Col flex={1}>
                    <CompactStatCard
                      loading={isSessionsLoading || isSessionsFetching}
                      label={t(
                        "media.projects.analytics.dashboard.uniqueVisitors"
                      )}
                      metric={sessionsData?.unique_visitors}
                    />
                  </Col>
                  <Col flex={1}>
                    <CompactStatCard
                      loading={isSessionsLoading || isSessionsFetching}
                      label={t(
                        "media.projects.analytics.dashboard.chatInteractions"
                      )}
                      metric={sessionsData?.chat_interactions}
                    />
                  </Col>
                </Row>
                <Row gutter={[8, 8]} style={{ width: "100%" }}>
                  <VideoSummary projectId={projectId} />
                  <DocumentSummary projectId={projectId} />
                  <ChatbotSummary projectId={projectId} />
                </Row>
                <Row gutter={[8, 8]} style={{ width: "100%" }}>
                  <Col span={24}>
                    <SessionsChart
                      projectId={projectId}
                      dateRange={dateRange}
                    />
                  </Col>
                </Row>
                {/* </Flex> */}
              </>
            )}
            {hasUnpublishedInstances && (
              <Alert
                message={t(
                  "media.projects.analytics.dashboard.previewModeDisclaimer"
                )}
                type="info"
                showIcon
              />
            )}
          </Flex>
          {organizationId && projectId ? (
            <InsightsPanel
              key={projectId}
              organizationId={organizationId}
              workspaceId={undefined}
              projectId={projectId}
            />
          ) : null}
        </Flex>
      </Flex>
    </Authenticated>
  );
}
